import { FunctionComponent, useEffect } from "react";
import i18n from "@/services/i18n";
import Head from "next/head";
import { useRouter } from "next/router";
import useIDOReferral from "@/hooks/ido/useIDOReferral";
import { currentRound, IDO_STAGE, IDOStage } from "@/services/ido";
import { Button } from "@mantine/core";
import UserInvestmentInfoDialog from "@/components/ido/UserInvestmentInfoDialog";
import { useDisclosure } from "@mantine/hooks";
import useAccount from "@/hooks/useAccount";

const InvestmentIframe: FunctionComponent<{
  investInfo: any;
  campaignId: string;
  campaignSrc: string;
}> = ({ investInfo, campaignId, campaignSrc }) => {
  const locale = investInfo.locale;
  const router = useRouter();
  const { referral } = useIDOReferral();
  const { address, login } = useAccount();

  const round = currentRound;
  const hideInvest =
    IDO_STAGE === IDOStage.ROUND_1_END || IDO_STAGE === IDOStage.ROUND_2_END;

  const isCN = router.pathname === "/ido/cn";

  const [
    investDialogOpened,
    { open: openInvestDialog, close: closeInvestDialog },
  ] = useDisclosure(false);

  console.log("campaignId", campaignId);
  console.log("campaignSrc", campaignSrc);

  return (
    <div className="flex flex-col items-center px-5 sm:px-0">
      <Head>
        <script async src={campaignSrc}></script>
      </Head>
      {hideInvest ? (
        <div className="text-center text-6xl sm:text-[100px] font-extrabold text-dark1 leading-none">
          <div>{i18n.t("initial", locale)}</div>
          <div className="text-primary">{i18n.t("dex", locale)}</div>
          <div>{i18n.t("offering", locale)}</div>
        </div>
      ) : (
        <div>
          <div className="text-3xl font-extrabold sm:text-5xl">
            {i18n.t("investment.title", locale)}
          </div>
          <div className="flex items-center mt-5 text-sm">
            <div>
              {i18n.t("target_goal", locale)}{" "}
              <span className="block font-semibold text-center sm:inline">
                {round === 1 ? "$0.5 million" : "$3.5 million"}
              </span>
            </div>
            <div className="h-6 w-[1px] bg-gray1 mx-4"></div>
            <div>
              {i18n.t("price", locale)}{" "}
              <span className="block font-semibold text-center sm:inline">
                {round === 1 ? "$0.03" : "$0.035"}
              </span>
            </div>
            <div className="h-6 w-[1px] bg-gray1 mx-4"></div>
            {round === 1 ? (
              <div>
                July <span className="font-semibold">19</span> - July{" "}
                <span className="font-semibold">21</span>
              </div>
            ) : (
              <div>
                July <span className="font-semibold">22</span> - August{" "}
                <span className="font-semibold">19</span>
              </div>
            )}
          </div>
        </div>
      )}

      {referral && (
        <div className="mt-8 p-8 bg-[#FFDB83] text-sm font-semibold text-center rounded-lg sm:w-[458px]">
          <div>You are visiting from a referral link.</div>
          {!isCN && (
            <div>Purchases made through the KOL link will receive a bonus.</div>
          )}
        </div>
      )}

      <div className="mt-8">
        {!hideInvest && (
          <div
            id="deshilling-button"
            data-campaign-id={campaignId}
            className="flex flex-col items-center sm:bg-white rounded-lg sm:w-[458px] sm:shadow-card1 overflow-hidden sm:h-[460px]"
          ></div>
        )}

        {IDO_STAGE === IDOStage.ROUND_1_END && (
          <div className="mt-8 shadow-card1 bg-[#E0E1E1] p-6 rounded-lg">
            <div
              className="px-4 py-2 text-sm font-semibold text-center rounded-lg"
              style={{
                background: "rgba(255, 255, 255, 0.50)",
              }}
            >
              Round one complete
            </div>
            <div className="flex items-center justify-center text-sm mt-7">
              <div>
                {i18n.t("investment.target_goal", locale)}{" "}
                <span className="font-semibold">$0.5 million</span>
              </div>
              <div className="h-6 w-[1px] bg-gray1 mx-4"></div>
              <div>
                {i18n.t("price", locale)}{" "}
                <span className="font-semibold">$0.03</span>
              </div>
              <div className="h-6 w-[1px] bg-gray1 mx-4"></div>
              <div>
                July <span className="font-semibold">19</span> - July{" "}
                <span className="font-semibold">22</span>
              </div>
            </div>
          </div>
        )}

        {round === 1 && (
          <div className="mt-8 shadow-card1 bg-[#FFDB83] p-6 rounded-lg">
            <div
              className="px-4 py-2 text-sm font-semibold text-center rounded-lg"
              style={{
                background: "rgba(255, 255, 255, 0.50)",
              }}
            >
              {i18n.t("investment.second_round_coming_soon", locale)}
            </div>
            <div className="flex items-center justify-center text-sm mt-7">
              <div>
                {i18n.t("investment.target_goal", locale)}{" "}
                <span className="font-semibold">
                  {i18n.t("investment.3million", locale)}
                </span>
              </div>
              <div className="h-6 w-[1px] bg-gray1 mx-4"></div>
              <div>
                {i18n.t("price", locale)}{" "}
                <span className="font-semibold">$0.035</span>
              </div>
              <div className="h-6 w-[1px] bg-gray1 mx-4"></div>
              <div>
                July <span className="font-semibold">22</span> - August{" "}
                <span className="font-semibold">19</span>
              </div>
            </div>
          </div>
        )}

        {IDO_STAGE === IDOStage.ROUND_2_END && (
          <div className="mt-8 shadow-card1 bg-[#E0E1E1] p-6 rounded-lg">
            <div
              className="px-4 py-2 text-sm font-semibold text-center rounded-lg"
              style={{
                background: "rgba(255, 255, 255, 0.50)",
              }}
            >
              Round two complete
            </div>
            <div className="flex items-center justify-center text-sm mt-7">
              <div>
                {i18n.t("investment.target_goal", locale)}{" "}
                <span className="font-semibold">
                  {i18n.t("investment.3million", locale)}
                </span>
              </div>
              <div className="h-6 w-[1px] bg-gray1 mx-4"></div>
              <div>
                {i18n.t("price", locale)}{" "}
                <span className="font-semibold">$0.035</span>
              </div>
              <div className="h-6 w-[1px] bg-gray1 mx-4"></div>
              <div>
                July <span className="font-semibold">22</span> - August{" "}
                <span className="font-semibold">19</span>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="mt-10">
        <Button
          className="w-[242px] bg-white h-[56px] text-sm"
          radius="xl"
          variant="outline"
          onClick={() => {
            if (!address) {
              return login();
            }
            openInvestDialog();
          }}
        >
          View my participation
        </Button>
      </div>
      <UserInvestmentInfoDialog
        opened={investDialogOpened}
        close={closeInvestDialog}
        campaignId={campaignId}
      ></UserInvestmentInfoDialog>
    </div>
  );
};

export default InvestmentIframe;
